import React, { useState } from 'react';
import { Phone, Mail, Home, Sparkles, Clock, Shield, Award, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import LocalBusinessSchema from '../components/LocalBusinessSchema';

const HomePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    cleaningType: '',
    squareFootage: '',
    preferredDate: '',
    specialRequirements: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, cleaningType: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quote-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          cleaning_type: formData.cleaningType,
          square_footage: formData.squareFootage,
          preferred_date: formData.preferredDate,
          special_requirements: formData.specialRequirements,
          message: formData.message
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit quote request');
      }

      const data = await response.json();
      console.log('Quote request submitted:', data);
      
      toast.success('Thank you! We\'ve received your request and will contact you soon.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        cleaningType: '',
        squareFootage: '',
        preferredDate: '',
        specialRequirements: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting quote request:', error);
      toast.error('Sorry, there was an error submitting your request. Please try again or call us directly.');
    }
  };

  return (
    <div className="min-h-screen">
      <SEO />
      <LocalBusinessSchema />
      
      {/* Hero Section */}
      <section className="hero-section relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1692133219782-070d4ac2aefb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHxjbGVhbiUyMGhvbWUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzU2MTgzMjJ8MA&ixlib=rb-4.1.0&q=85&w=1920" 
            alt="Clean home"
            className="w-full h-full object-cover"
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/50"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10 text-white">
          <div className="max-w-3xl">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight animate-slide-up">
              Texas House Cleaning
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-slate-200 animate-slide-up-delay">
              Professional residential cleaning services that make your home sparkle. 
              Trust, quality, and attention to detail in every clean.
            </p>
            <div className="flex gap-4 animate-slide-up-delay-2">
              <Button 
                size="lg" 
                className="bg-texas-red hover:bg-texas-red-dark text-white px-8 py-6 text-lg"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              >
                Get A Free Quote
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg"
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
              >
                Our Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-slate-900 mb-4">Our Cleaning Services</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Professional cleaning solutions tailored to your needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="service-card group hover:shadow-2xl transition-all duration-300 border-0 ring-2 ring-texas-blue">
              <div className="overflow-hidden rounded-t-lg relative">
                <div className="absolute top-4 right-4 bg-texas-red text-white px-4 py-2 rounded-full text-sm font-bold z-10">
                  POPULAR
                </div>
                <img 
                  src="https://images.pexels.com/photos/6195114/pexels-photo-6195114.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940&fit=crop"
                  alt="Standard Cleaning"
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                />
              </div>
              <CardContent className="p-8">
                <Sparkles className="h-12 w-12 text-texas-blue mb-4" />
                <h3 className="text-2xl font-bold mb-4 text-slate-900">Standard Cleaning</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Regular maintenance cleaning to keep your home fresh and tidy. Perfect for weekly or bi-weekly schedules.
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start">
                    <span className="text-texas-red mr-2">✓</span>
                    Dusting all surfaces
                  </li>
                  <li className="flex items-start">
                    <span className="text-texas-red mr-2">✓</span>
                    Vacuuming & mopping floors
                  </li>
                  <li className="flex items-start">
                    <span className="text-texas-red mr-2">✓</span>
                    Kitchen & bathroom cleaning
                  </li>
                  <li className="flex items-start">
                    <span className="text-texas-red mr-2">✓</span>
                    Trash removal
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="service-card group hover:shadow-2xl transition-all duration-300 border-0">
              <div className="overflow-hidden rounded-t-lg">
                <img 
                  src="https://images.pexels.com/photos/6196566/pexels-photo-6196566.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940&fit=crop"
                  alt="Deep Cleaning"
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                />
              </div>
              <CardContent className="p-8">
                <Sparkles className="h-12 w-12 text-texas-blue mb-4" />
                <h3 className="text-2xl font-bold mb-4 text-slate-900">Deep Cleaning</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Comprehensive top-to-bottom cleaning that tackles every corner of your home. Ideal for seasonal refresh.
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start">
                    <span className="text-texas-red mr-2">✓</span>
                    Everything in standard cleaning
                  </li>
                  <li className="flex items-start">
                    <span className="text-texas-red mr-2">✓</span>
                    Baseboards & door frames
                  </li>
                  <li className="flex items-start">
                    <span className="text-texas-red mr-2">✓</span>
                    Inside cabinets & appliances
                  </li>
                  <li className="flex items-start">
                    <span className="text-texas-red mr-2">✓</span>
                    Window cleaning
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="service-card group hover:shadow-2xl transition-all duration-300 border-0">
              <div className="overflow-hidden rounded-t-lg">
                <img 
                  src="https://images.unsplash.com/photo-1647381518264-97ff1835026f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxob3VzZSUyMGNsZWFuaW5nfGVufDB8fHx8MTc3NTYxODMyNnww&ixlib=rb-4.1.0&q=85&w=940&fit=crop"
                  alt="Move-Out Cleaning"
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                />
              </div>
              <CardContent className="p-8">
                <Home className="h-12 w-12 text-texas-blue mb-4" />
                <h3 className="text-2xl font-bold mb-4 text-slate-900">Move-Out Cleaning</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Thorough cleaning for moving in or out. Get your deposit back or welcome new residents to a spotless home.
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start">
                    <span className="text-texas-red mr-2">✓</span>
                    Complete deep cleaning
                  </li>
                  <li className="flex items-start">
                    <span className="text-texas-red mr-2">✓</span>
                    All rooms & closets
                  </li>
                  <li className="flex items-start">
                    <span className="text-texas-red mr-2">✓</span>
                    Garage & storage areas
                  </li>
                  <li className="flex items-start">
                    <span className="text-texas-red mr-2">✓</span>
                    Move-in ready certification
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-slate-900 mb-4">Why Choose Texas House Cleaning</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Experience the difference with our professional cleaning team
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6 hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-texas-blue/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="h-10 w-10 text-texas-blue" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">Trusted & Insured</h3>
              <p className="text-slate-600">
                Fully licensed and insured professionals you can trust in your home
              </p>
            </div>

            <div className="text-center p-6 hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-texas-red/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="h-10 w-10 text-texas-red" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">Quality Guaranteed</h3>
              <p className="text-slate-600">
                100% satisfaction guarantee or we'll make it right at no extra cost
              </p>
            </div>

            <div className="text-center p-6 hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-texas-blue/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="h-10 w-10 text-texas-blue" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">Flexible Scheduling</h3>
              <p className="text-slate-600">
                We work around your schedule with convenient booking options
              </p>
            </div>

            <div className="text-center p-6 hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-texas-red/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Sparkles className="h-10 w-10 text-texas-red" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">Eco-Friendly Products</h3>
              <p className="text-slate-600">
                Safe, non-toxic cleaning products that protect your family and pets
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            <div className="flex justify-center items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_house-cleaning-quote-1/artifacts/pm7fs2qg_cleaning_guy1-removebg-preview.png"
                alt="Professional Cleaning Service"
                className="w-80 h-80 object-contain"
                decoding="async"
              />
            </div>
            <div>
              <h2 className="text-5xl font-bold text-slate-900 mb-6">About Texas House Cleaning</h2>
              <p className="text-lg text-slate-700 mb-6 leading-relaxed">
                We're a locally owned and operated cleaning service dedicated to providing exceptional residential cleaning throughout Texas. With years of experience and a passion for perfection, we treat every home as if it were our own.
              </p>
              <p className="text-lg text-slate-700 mb-6 leading-relaxed">
                Our team of trained professionals uses the latest cleaning techniques and eco-friendly products to deliver outstanding results every time. We understand that your home is your sanctuary, and we're committed to maintaining the highest standards of cleanliness and care.
              </p>
              <div className="grid grid-cols-2 gap-6 mt-8">
                <div>
                  <div className="text-4xl font-bold text-texas-blue mb-2">500+</div>
                  <div className="text-slate-600">Happy Clients</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-texas-blue mb-2">5+ Years</div>
                  <div className="text-slate-600">Experience</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-texas-red mb-2">100%</div>
                  <div className="text-slate-600">Satisfaction Rate</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-texas-red mb-2">24/7</div>
                  <div className="text-slate-600">Customer Support</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-slate-900 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied customers
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-500 fill-current" />
                  ))}
                </div>
                <p className="text-slate-700 mb-6 italic leading-relaxed">
                  "Texas House Cleaning transformed my home! Their deep cleaning service was incredible - every corner sparkled. The team was professional, punctual, and respectful. Highly recommend!"
                </p>
                <div className="flex items-center">
                  <div className="bg-texas-blue text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg mr-4">
                    SM
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">Sarah Martinez</div>
                    <div className="text-sm text-slate-500">Austin, TX</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-500 fill-current" />
                  ))}
                </div>
                <p className="text-slate-700 mb-6 italic leading-relaxed">
                  "Used their move-out cleaning service and got my full deposit back! They cleaned areas I didn't even know existed. Worth every penny. Will definitely use again!"
                </p>
                <div className="flex items-center">
                  <div className="bg-texas-red text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg mr-4">
                    JT
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">James Thompson</div>
                    <div className="text-sm text-slate-500">Dallas, TX</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-500 fill-current" />
                  ))}
                </div>
                <p className="text-slate-700 mb-6 italic leading-relaxed">
                  "As a busy mom, Texas House Cleaning has been a lifesaver. Reliable, thorough, and they use eco-friendly products which is important for my kids. Thank you!"
                </p>
                <div className="flex items-center">
                  <div className="bg-texas-blue text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg mr-4">
                    LR
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">Lisa Rodriguez</div>
                    <div className="text-sm text-slate-500">Houston, TX</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-24 bg-slate-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-5xl font-bold mb-4">Get Your Free Quote</h2>
              <p className="text-xl text-slate-300">
                Tell us about your cleaning needs and we'll get back to you within 24 hours
              </p>
            </div>

            <Card className="border-0 shadow-2xl">
              <CardContent className="p-8 md:p-12">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Full Name *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        required
                        className="h-12"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="john@example.com"
                        required
                        className="h-12"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Phone Number *
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(555) 123-4567"
                        required
                        className="h-12"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Service Address *
                      </label>
                      <Input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="123 Main St, Austin, TX"
                        required
                        className="h-12"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Cleaning Type *
                      </label>
                      <Select onValueChange={handleSelectChange} value={formData.cleaningType}>
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Select cleaning type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="standard">Standard Cleaning</SelectItem>
                          <SelectItem value="deep">Deep Cleaning</SelectItem>
                          <SelectItem value="moveout">Move-Out Cleaning</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Square Footage
                      </label>
                      <Input
                        type="text"
                        name="squareFootage"
                        value={formData.squareFootage}
                        onChange={handleInputChange}
                        placeholder="e.g., 1500 sq ft"
                        className="h-12"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Preferred Date
                    </label>
                    <Input
                      type="date"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleInputChange}
                      className="h-12"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Special Requirements
                    </label>
                    <Input
                      type="text"
                      name="specialRequirements"
                      value={formData.specialRequirements}
                      onChange={handleInputChange}
                      placeholder="Pets, allergies, specific areas of focus, etc."
                      className="h-12"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Additional Message
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us more about your cleaning needs..."
                      rows={4}
                      className="resize-none"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-texas-red hover:bg-texas-red-dark text-white h-14 text-lg font-semibold"
                  >
                    Request Free Quote
                  </Button>
                </form>

                <div className="mt-12 pt-8 border-t border-slate-200">
                  <div className="grid md:grid-cols-2 gap-6 text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start">
                      <Phone className="h-6 w-6 text-texas-blue mr-3" />
                      <div>
                        <div className="text-sm text-slate-500">Call Us</div>
                        <div className="font-bold text-slate-900">(346) 203-1728</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-center md:justify-start">
                      <Mail className="h-6 w-6 text-texas-blue mr-3" />
                      <div>
                        <div className="text-sm text-slate-500">Email Us</div>
                        <div className="font-bold text-slate-900">clean.texashouse@gmail.com</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
